import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { View, ALL_TRADES_VIEW, type TabCounterparty } from '@/types';

const toSlug = (name: string): string => {
  const base = name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return base || 'view';
};

/** Generate a unique slug by appending a numeric suffix if needed */
const uniqueSlug = (name: string, existingSlugs: string[]): string => {
  const base = toSlug(name);
  if (!existingSlugs.includes(base)) return base;
  let i = 2;
  while (existingSlugs.includes(`${base}-${i}`)) i++;
  return `${base}-${i}`;
};

// crypto.randomUUID() is only defined in secure contexts (HTTPS or localhost). Tab
// creation runs on every Trades load, so if the app is opened over plain http (e.g.
// http://<lan-ip>:3000 from a teammate's machine) the call would throw and the page would
// never render. Fall back to a counterparty-keyed pseudo-id that's still stable within the
// session.
const newViewId = (cp: string): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `auto-${cp}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const counterpartyView = (
  cp: TabCounterparty,
  existingSlugs: string[],
  isAutoGenerated: boolean,
): View => {
  const slug = uniqueSlug(cp.name, existingSlugs);
  existingSlugs.push(slug);
  return {
    id: newViewId(cp.name),
    name: cp.name,
    slug,
    isSystem: false,
    isAutoGenerated,
    counterparties: [cp.name],
    counterpartyId: cp.id,
    isInactive: !cp.isActive,
  };
};

const isCounterpartyTab = (v: View): boolean => !v.isSystem && v.counterparties.length === 1;

/** A tab persisted before ids carries only the name. */
const isTabFor = (v: View, cp: TabCounterparty): boolean =>
  isCounterpartyTab(v) &&
  (v.counterpartyId !== undefined ? v.counterpartyId === cp.id : v.counterparties[0] === cp.name);

/**
 * Tab order: [system tabs] → [auto tabs, in the order of `cps`] → [tabs opened by hand, in
 * order]. Auto tabs are always open.
 */
const orderTabs = (views: View[], openTabs: string[], cps: TabCounterparty[]): string[] => {
  const byId = new Map(views.map((v) => [v.id, v]));
  const rank = new Map(cps.map((cp, i) => [cp.id, i]));
  const system: string[] = [];
  const auto: View[] = [];
  const other: string[] = [];
  const seen = new Set<string>();
  for (const id of [...openTabs, ...views.filter((v) => v.isAutoGenerated).map((v) => v.id)]) {
    const v = byId.get(id);
    if (!v || seen.has(id)) continue;
    seen.add(id);
    if (v.isSystem) system.push(id);
    else if (v.isAutoGenerated) auto.push(v);
    else other.push(id);
  }
  auto.sort((a, b) => (rank.get(a.counterpartyId!) ?? 0) - (rank.get(b.counterpartyId!) ?? 0));
  return [...system, ...auto.map((v) => v.id), ...other];
};

interface ViewsStore {
  // State
  views: View[];
  activeViewId: string;
  openTabs: string[];

  /**
   * Make the auto tabs exactly `cps`, in that order (the first page of the server's tab
   * list): create the missing ones, refresh names, ids and the inactive flag, and drop an
   * auto tab whose counterparty is no longer listed, unless it is the open one, which stays
   * as a tab opened by hand.
   */
  syncCounterpartyTabs: (cps: TabCounterparty[]) => void;
  /** Open (or switch to) the tab of a counterparty picked from the search. */
  openCounterpartyTab: (cp: TabCounterparty) => void;
  /** Close a tab opened from the search; auto tabs and All Trades stay. */
  closeTab: (viewId: string) => void;

  // Tab management
  activate: (viewId: string) => void;
  openTab: (viewId: string) => void;
}

export const useViewsStore = create<ViewsStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        views: [ALL_TRADES_VIEW],
        activeViewId: 'all-trades',
        openTabs: ['all-trades'],

        syncCounterpartyTabs: (cps) => {
          const state = get();
          const claimed = new Set<number>();
          const views: View[] = [];
          for (const v of state.views) {
            if (!isCounterpartyTab(v)) {
              views.push(v);
              continue;
            }
            const cp = cps.find((c) => isTabFor(v, c));
            if (cp) {
              // A second tab for one counterparty (two pre-id tabs, or one opened by hand
              // before its auto tab existed) is dropped.
              if (claimed.has(cp.id)) continue;
              claimed.add(cp.id);
              views.push({
                ...v,
                name: cp.name,
                counterparties: [cp.name],
                counterpartyId: cp.id,
                isAutoGenerated: true,
                isInactive: !cp.isActive,
              });
            } else if (!v.isAutoGenerated) {
              views.push(v);
            } else if (v.id === state.activeViewId) {
              // Off the first page while open: kept, and now closes with its X.
              views.push({ ...v, isAutoGenerated: false });
            }
          }
          const existingSlugs = views.map((v) => v.slug);
          for (const cp of cps) {
            if (!claimed.has(cp.id)) views.push(counterpartyView(cp, existingSlugs, true));
          }

          const openTabs = orderTabs(views, state.openTabs, cps);
          const activeViewId = views.some((v) => v.id === state.activeViewId)
            ? state.activeViewId
            : ALL_TRADES_VIEW.id;
          // No-op guard: skip the set() when nothing changed, so the effect calling this on
          // every list refresh does not re-render the page.
          if (
            activeViewId === state.activeViewId &&
            JSON.stringify(openTabs) === JSON.stringify(state.openTabs) &&
            JSON.stringify(views) === JSON.stringify(state.views)
          ) {
            return;
          }
          set({ views, openTabs, activeViewId });
        },

        openCounterpartyTab: (cp) => {
          const state = get();
          const existing = state.views.find((v) => isTabFor(v, cp));
          if (existing) {
            // The pick carries the registry's current name and status (and the id a tab
            // persisted before ids lacks); a tab off the first page is refreshed only here.
            const fresh: View = {
              ...existing,
              name: cp.name,
              counterparties: [cp.name],
              counterpartyId: cp.id,
              isInactive: !cp.isActive,
            };
            if (JSON.stringify(fresh) !== JSON.stringify(existing)) {
              set({ views: state.views.map((v) => (v.id === existing.id ? fresh : v)) });
            }
            get().openTab(existing.id);
            return;
          }
          const view = counterpartyView(cp, state.views.map((v) => v.slug), false);
          set({
            views: [...state.views, view],
            openTabs: [...state.openTabs, view.id],
            activeViewId: view.id,
          });
        },

        closeTab: (viewId) => {
          const state = get();
          const view = state.views.find((v) => v.id === viewId);
          if (!view || view.isSystem || view.isAutoGenerated) return;
          set({
            views: state.views.filter((v) => v.id !== viewId),
            openTabs: state.openTabs.filter((id) => id !== viewId),
            activeViewId:
              state.activeViewId === viewId ? ALL_TRADES_VIEW.id : state.activeViewId,
          });
        },

        // Tab management
        activate: (viewId) => {
          if (!get().views.some((v) => v.id === viewId)) return;
          set({ activeViewId: viewId });
        },

        openTab: (viewId) => {
          if (!get().views.some((v) => v.id === viewId)) return;

          const state = get();
          if (state.openTabs.includes(viewId)) {
            set({ activeViewId: viewId });
          } else {
            set((state) => ({
              openTabs: [...state.openTabs, viewId],
              activeViewId: viewId,
            }));
          }
        },
      }),
      {
        name: 'views-store',
        version: 1,
        partialize: (state) => ({
          views: state.views,
          openTabs: state.openTabs,
          activeViewId: state.activeViewId,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Drop legacy user-created views persisted before the views feature
            // was simplified to read-only counterparty tabs. Only the canonical
            // All Trades system tab, auto-generated per-CP tabs, tabs opened
            // from the search (they carry the counterparty id) and a pre-id auto
            // tab that left the tab list while open (named after its counterparty)
            // survive.
            // (Earlier transient experiments persisted extra "system" views like
            // stock-lb / repo — narrow the filter to the canonical id so they
            // get dropped on next rehydrate.)
            state.views = state.views.filter(
              (v: View) =>
                v.id === ALL_TRADES_VIEW.id ||
                v.isAutoGenerated ||
                v.counterpartyId !== undefined ||
                (!v.isSystem && v.counterparties.length === 1 && v.name === v.counterparties[0])
            );

            // Ensure ALL_TRADES_VIEW is always present (DATA-02)
            const hasSystemView = state.views.some(
              (v: View) => v.id === ALL_TRADES_VIEW.id
            );
            if (!hasSystemView) {
              state.views = [ALL_TRADES_VIEW, ...state.views];
            }

            // Drop tabs that point at views we just removed.
            const liveIds = new Set(state.views.map((v: View) => v.id));
            state.openTabs = state.openTabs.filter((id: string) => liveIds.has(id));

            // Ensure all-trades is always in openTabs
            if (!state.openTabs.includes('all-trades')) {
              state.openTabs = ['all-trades', ...state.openTabs];
            }

            // If the active view was dropped, fall back to All Trades.
            if (!liveIds.has(state.activeViewId)) {
              state.activeViewId = 'all-trades';
            }
          }
        },
      }
    ),
    { name: 'views-store', enabled: import.meta.env.DEV }
  )
);
